/**
 * Pill-row filter that allows more than one selection.
 *
 * Replaces the single `<select>` filters that made it impossible to view, say,
 * test drives and deliveries together — the specific gap raised in the
 * September showcase ("you can only single click"). Also used by the lead and
 * customer lists so the interaction is the same everywhere.
 *
 * An empty selection means "all", which is what a dealer expects from a filter
 * row: clearing it shows everything rather than nothing.
 */

import { useMemo } from 'react';

/**
 * @param label      short caps label shown before the pills, e.g. "TYPE"
 * @param options    [{ value, label, color? }]
 * @param value      Set | array of selected values
 * @param onChange   (Set) => void
 * @param allLabel   text for the clear-all pill (default "All")
 * @param disabled   render read-only
 */
export default function MultiSelectFilter({
  label,
  options = [],
  value,
  onChange,
  allLabel = 'All',
  disabled = false,
}) {
  const selected = useMemo(
    () => (value instanceof Set ? value : new Set(value || [])),
    [value],
  );

  const toggle = (v) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  const clear = () => { if (!disabled) onChange(new Set()); };

  return (
    <div
      className="flex-row"
      style={{ alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
      role="group"
      aria-label={label}
    >
      {label && (
        <span className="card-label" style={{ marginRight: 2 }}>{label}</span>
      )}

      <button
        type="button"
        className={`pill${selected.size === 0 ? ' active' : ''}`}
        onClick={clear}
        disabled={disabled}
        aria-pressed={selected.size === 0}
      >
        {allLabel}
      </button>

      {options.map((o) => {
        const on = selected.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={`pill${on ? ' active' : ''}`}
            onClick={() => toggle(o.value)}
            disabled={disabled}
            aria-pressed={on}
            title={o.title || o.label}
            style={o.color && on
              ? { borderColor: o.color, color: o.color }
              : undefined}
          >
            {o.icon ? `${o.icon} ` : ''}{o.label}
            {typeof o.count === 'number' ? ` ${o.count}` : ''}
          </button>
        );
      })}
    </div>
  );
}

/** Stable dependency key for a Set, for useApi/useMemo deps arrays. */
export function selectionKey(set) {
  if (!set) return '';
  return [...(set instanceof Set ? set : new Set(set))].sort().join(',');
}

/** Serialise a selection for an API query param — undefined when empty (= all). */
export function selectionParam(set) {
  const k = selectionKey(set);
  return k || undefined;
}
